import React, { createContext, lazy, useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Switch,
  Route,
  Redirect,
} from "react-router-dom";
import AccessibleNavigationAnnouncer from "./components/AccessibleNavigationAnnouncer";
import Modals from "./components/Modals";
import Alerts from "./components/Alerts";
import { Button } from "@windmill/react-ui";
import {
  getFirebaseToken,
  getOrRegisterServiceWorker,
  onForegroundMessage,
} from "./firebase";
import Buttons from "./pages/Buttons";
import { axiosConnect } from "./auth/axios.connect";
import "./App.css";
import { userConfig } from "./auth/use.cache";

const Layout = lazy(() => import("./containers/Layout"));
const Login = lazy(() => import("./pages/Login"));
const CreateAccount = lazy(() => import("./pages/CreateAccount"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ConfirmPassword = lazy(() => import("./pages/confirmPin"));
const FullBodyScan = lazy(() => import("./pages/360"));
export const context = createContext(null);

function App() {
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [btnEnable, setBtnEnable] = useState("Enable");
  const [modal, setModal] = useState({
    isOpen: false,
    countdown: false,
    message: "",
    error: false,
    description: "",
    btnTxt: "Accept",
    link: "",
    firstTime:false
  });
  const [notification, setNotification] = useState({ title: "", body: "" });
  const [enableMagicFix, setEnableMagicFix] = useState(false);
  const [user_id, setUser_id] = useState(null);
  const [search, setSearch] = useState("");
  const [itIsFirstTime, setItIsFirstTime] = useState(false);
  const [alert, setAlert] = useState({
    isOpen: true,
    message: "",
    type: "normal",
    description: "",
  });

  useEffect(() => {
    getOrRegisterServiceWorker()
      .then((serviceWorker) => {
        console.log({ serviceWorker });
      })
      .catch((error) => {
        console.error("Error getting or registering service worker:", error);
      });
  }, []);

  useEffect(() => {
    if (sessionStorage.getItem("user_item")) {
      setUser_id(JSON.parse(sessionStorage.getItem("user_item")).user_id);
    } else {
      setUser_id(null);
    }
  }, []);

  useEffect(() => {
    onForegroundMessage()
      .then((payload) => {
        console.log("Received foreground message:", payload);
        const {
          notification: { title, body },
        } = payload;

        setModal((prevModal) => ({
          ...prevModal,
          isOpen: true,
          countdown: false,
          message: title,
          description: body,
          firstTime:false
        }));
      })
      .catch((err) =>
        console.error("Error retrieving foreground message:", err)
      );
  });

  const [showNotificationBanner, setShowNotificationBanner] = useState(false);
  const [
    notificationPermissionGranted,
    setNotificationPermissionGranted,
  ] = useState(false);

  const registerDevice = async (token) => {
    try {
      console.log({ token });
      const { data } = await axiosConnect.post(
        "/user/register-device-for-notification",
        {
          user_id,
          fcm_token: token,
        }
      );
      setAlert({
        isOpen: true,
        message: `The device is ready to receive messages.`,
        type: "normal",
        description: "",
      });
    } catch (AxiosError) {
      let errorMessage =
        AxiosError.response?.data?.message ||
        AxiosError.message ||
        "Request failed";
      console.error("Error message:", errorMessage);
      if (errorMessage == "An issue has arisen. Please try again later.") {
        errorMessage =
          "Please ensure that you are a waitlist member and use the email to send us a message";
      }

      setAlert({
        isOpen: true,
        message: `${errorMessage}`,
        type: "error",
        description: "",
      });
    }
  };

  const handleGetFirebaseToken = async () => {
    try {
      if (!notificationPermissionGranted && !localStorage.getItem("premission")) {
        const authStatus = await Notification.requestPermission();
        console.log({ authStatus });

        if (authStatus === "granted") {
          setNotificationPermissionGranted(true);
          setShowNotificationBanner(false);


          
        } else {
          setShowNotificationBanner(true);
          setBtnEnable("Enable");
          return; // Exit if permission is not granted
        }
      }

      console.log("Sending request...");
      setBtnEnable("Requesting...");

      // Your logic to get firebase token
      const firebaseToken = await getFirebaseToken();

      if (firebaseToken) {
        console.log("Registering device...");
        const registered = await registerDevice(firebaseToken);
        setBtnEnable("Done");
        localStorage.setItem("premission", true);

        // Show success modal
        setModal((prevModal) => ({
          ...prevModal,
          isOpen: true,
          countdown: true,
          message: "DevSensor Notification Enabled",
          description: "You'll get posture alerts and workout recommendation",
        }));
      } else {
        // Handle case where firebase token is not retrieved
        console.error("Failed to retrieve Firebase token.");
        setBtnEnable("Enable");
      }
    } catch (error) {
      console.error("An error occurred while handling Firebase token.", error);
      setBtnEnable("Enable");
    }
  };

  useEffect(() => {
    if(!localStorage.getItem("premission")){
      setShowNotificationBanner(true);
    }
  },[]);

  useEffect(() => {
    if (modal.countdown) {
      const timer = setTimeout(() => {
        setModal((prevModal) => ({
          ...prevModal,
          isOpen: false,
          countdown: false,
        }));
      }, 7000);

      return () => clearTimeout(timer);
    }
  }, [modal.isOpen]);

  useEffect(() => {
    if (alert.isOpen) {
      const timer = setTimeout(() => {
        setAlert((prevAlert) => ({
          ...prevAlert,
          isOpen: false,
        }));
      }, 5100);

      return () => clearTimeout(timer);
    }
  }, [alert.isOpen]);

  return (
    <div className="body">
      <context.Provider
        value={{
          search,
          setSearch,
          modalIsOpen,
          setModalIsOpen,
          modal,
          setModal,
          alert,
          setAlert,
          enableMagicFix,
          setEnableMagicFix,
          itIsFirstTime,
          setItIsFirstTime,
        }}
      >
        <Router>
          {user_id && showNotificationBanner && (
            <div className="notification-banner bg-white text-black p-4 flex justify-between">
              <span className="p-1 m-1">
                Allow DevSensor to send personalized notifications to you.
              </span>
              <Button
                layout="outline"
                style={{ color:"black" }}
                className="notification-banner-link border-gray-700 text-black"
                onClick={handleGetFirebaseToken}
                disabled={btnEnable === "Requesting..."}
              >
                {btnEnable}
              </Button>
            </div>
          )}

          <AccessibleNavigationAnnouncer />
          <Modals />
          <Switch>
            <Route path="/login" component={Login} />
            <Route path="/create-account" component={CreateAccount} />
            <Route path="/forgot-password" component={ForgotPassword} />
            <Route path="/reset-password" component={ConfirmPassword} />
            <Route path="/body-scan" component={FullBodyScan} />

            {/* Place new routes over this */}

            <Route path="/" component={Layout} />

            {/* If you have an index page, you can remo this Redirect */}

            <Redirect exact from="/" to="/login" />
          </Switch>
        </Router>
      </context.Provider>
    </div>
  );
}

export default App;
